import { useSubscription } from './useSubscription';
import { useAuth } from '../contexts/AuthContext';

export interface PlanPermissions {
  loading: boolean;
  /* Reports & Analytics */
  canAccessReports: boolean;
  canExportCsv: boolean;
  canExportPdf: boolean;
  canAccessAdvancedInsights: boolean;
  /* Insights Module */
  canAccessInsightsModule: boolean;
  insightsLevel: 'basic' | 'advanced' | 'full';
  maxInsightsPerPeriod: number;
  canAccessHistoricalInsights: boolean;
  canAccessRecommendations: boolean;
  /* Calendar Module */
  canAccessCalendar: boolean;
  canCreateManualCalendarEvents: boolean;
  maxCalendarEventsPerMonth: number | null;
  canAccessCalendarSync: boolean;
  canAccessAdvancedCalendarFilters: boolean;
  /* Features */
  canUseMultipleCurrencies: boolean;
  canAccessLoansModule: boolean;
  canAccessRecurringAdvanced: boolean;
  canAccessPrioritySupport: boolean;
  /* Payees module */
  canUsePayeesModule: boolean;
  payeesLimit: number | null;
  /* AI Assistant */
  canUseAiAssistant: boolean;
  aiMonthlyQuestionsLimit: number | null;
  canUseAiAdvancedInsights: boolean;
  canUseAiProactiveAlerts: boolean;
  /* Limits */
  accountsLimit: number;
  cardsLimit: number;
  transactionsLimit: number;
  goalsLimit: number;
  /* Plan metadata */
  isPaid: boolean;
  isFree: boolean;
  isTrial: boolean;
  planName: string;
  planSlug: string;
  planPrice: number;
  planPriceMonthly: number;
  planPriceAnnual: number;
  annualDiscountPercent: number;
  trialAvailable: boolean;
  trialDays: number;
}

/**
 * usePlanPermissions — single source of truth for all feature access checks.
 *
 * Values come directly from the `plans` table (via useSubscription), which is
 * configured by the master admin. Any change in the admin panel reflects here
 * automatically, without code changes per feature.
 */
export const usePlanPermissions = (): PlanPermissions => {
  const { plan, isPaid, isFree, isTrial, loading } = useSubscription();
  const { role } = useAuth();

  const ADMIN_ROLES = ['master_admin', 'admin', 'support', 'finance'];
  const isAdmin = !!role && ADMIN_ROLES.includes(role);

  // Admins always have full access to all features
  if (isAdmin) {
    return {
      loading,
      canAccessReports: true,
      canExportCsv: true,
      canExportPdf: true,
      canAccessAdvancedInsights: true,
      canAccessInsightsModule: true,
      insightsLevel: 'full',
      maxInsightsPerPeriod: 9999,
      canAccessHistoricalInsights: true,
      canAccessRecommendations: true,
      canAccessCalendar: true,
      canCreateManualCalendarEvents: true,
      maxCalendarEventsPerMonth: null,
      canAccessCalendarSync: true,
      canAccessAdvancedCalendarFilters: true,
      canUseMultipleCurrencies: true,
      canAccessLoansModule: true,
      canAccessRecurringAdvanced: true,
      canAccessPrioritySupport: true,
      canUsePayeesModule: true,
      payeesLimit: null,
      canUseAiAssistant: true,
      aiMonthlyQuestionsLimit: null,
      canUseAiAdvancedInsights: true,
      canUseAiProactiveAlerts: true,
      accountsLimit: -1,
      cardsLimit: -1,
      transactionsLimit: -1,
      goalsLimit: -1,
      isPaid: true,
      isFree: false,
      isTrial: false,
      planName: 'Admin Full',
      planSlug: 'admin',
      planPrice: 0,
      planPriceMonthly: 0,
      planPriceAnnual: 0,
      annualDiscountPercent: 0,
      trialAvailable: false,
      trialDays: 0,
    };
  }

  return {
    loading,

    /* Reports & Analytics — driven by plan.reports_enabled */
    canAccessReports: plan?.reports_enabled ?? false,
    canExportCsv: plan?.can_export_csv ?? false,
    canExportPdf: plan?.can_export_pdf ?? false,
    canAccessAdvancedInsights: plan?.can_access_advanced_insights ?? false,

    /* Insights Module — driven by dedicated plan flags */
    canAccessInsightsModule: plan?.can_access_insights_module ?? true,
    insightsLevel: plan?.insights_level ?? 'basic',
    maxInsightsPerPeriod: plan?.max_insights_per_period ?? 3,
    canAccessHistoricalInsights: plan?.can_access_historical_insights ?? false,
    canAccessRecommendations: plan?.can_access_recommendations ?? false,

    /* Calendar Module */
    canAccessCalendar: plan?.can_access_calendar ?? true,
    canCreateManualCalendarEvents: plan?.can_create_manual_calendar_events ?? true,
    maxCalendarEventsPerMonth: plan?.max_calendar_events_per_month === -1 ? null : (plan?.max_calendar_events_per_month ?? 5),
    canAccessCalendarSync: plan?.can_access_calendar_sync ?? false,
    canAccessAdvancedCalendarFilters: plan?.can_access_advanced_calendar_filters ?? false,

    /* Features — driven by dedicated plan flags */
    canUseMultipleCurrencies: plan?.can_use_multiple_currencies ?? false,
    canAccessLoansModule: plan?.can_access_loans_module ?? false,
    canAccessRecurringAdvanced: plan?.can_access_recurring_advanced ?? false,
    canAccessPrioritySupport: plan?.can_access_priority_support ?? false,

    /* Payees module */
    canUsePayeesModule: plan?.can_use_payees_module ?? true,
    payeesLimit: (plan?.payees_limit === -1 || plan?.payees_limit === null) ? null : (plan?.payees_limit ?? null),

    /* AI Assistant */
    canUseAiAssistant: plan?.can_use_ai_assistant ?? true,
    aiMonthlyQuestionsLimit: (plan?.ai_monthly_questions_limit === -1 || plan?.ai_monthly_questions_limit === null) ? null : (plan?.ai_monthly_questions_limit ?? 10),
    canUseAiAdvancedInsights: plan?.can_use_ai_advanced_insights ?? false,
    canUseAiProactiveAlerts: plan?.can_use_ai_proactive_alerts ?? false,

    /* Quantitative limits */
    accountsLimit: plan?.accounts_limit ?? 1,
    cardsLimit: plan?.cards_limit ?? 1,
    transactionsLimit: plan?.monthly_transactions_limit ?? 30,
    goalsLimit: plan?.goals_limit ?? 2,

    /* Plan identity */
    isPaid: isPaid ?? false,
    isFree: isFree ?? true,
    isTrial: isTrial ?? false,
    planName: plan?.name ?? 'Gratuito',
    planSlug: plan?.slug ?? 'free',
    planPrice: plan?.price_brl ?? 0,
    planPriceMonthly: plan?.price_monthly ?? 0,
    planPriceAnnual: plan?.price_annual ?? 0,
    annualDiscountPercent: plan?.annual_discount_percent ?? 0,
    trialAvailable: plan?.trial_available ?? false,
    trialDays: plan?.trial_days ?? 0,
  };
};
